import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCartStore } from '../stores/cartStore';
import useIsMobile from '../hooks/useIsMobile';

const Logo = ({ width = 220 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={width} viewBox="33 44 614 145" style={{ display: 'block' }}>
    <defs>
      <linearGradient id="hgold" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#fffadc"/>
        <stop offset="18%" stopColor="#f2cc58"/>
        <stop offset="52%" stopColor="#c49020"/>
        <stop offset="82%" stopColor="#9a7010"/>
        <stop offset="100%" stopColor="#5a3c00"/>
      </linearGradient>
      <linearGradient id="hjyoti" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#fff0f5"/>
        <stop offset="35%" stopColor="#f0c8d8"/>
        <stop offset="100%" stopColor="#b07090"/>
      </linearGradient>
      <linearGradient id="hheart" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#c0385a"/>
        <stop offset="100%" stopColor="#7a0a2a"/>
      </linearGradient>
      <linearGradient id="hhline" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="transparent"/>
        <stop offset="25%" stopColor="rgba(180,130,20,0.4)"/>
        <stop offset="50%" stopColor="rgba(240,200,80,0.9)"/>
        <stop offset="75%" stopColor="rgba(180,130,20,0.4)"/>
        <stop offset="100%" stopColor="transparent"/>
      </linearGradient>
      <filter id="hg1"><feGaussianBlur stdDeviation="2.5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
      <filter id="hg2"><feGaussianBlur stdDeviation="4" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
      <filter id="hhg"><feGaussianBlur stdDeviation="3" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
    </defs>
    <text x="340" y="118" textAnchor="middle" fontFamily="Georgia,'Times New Roman',serif" fontSize="76" fontWeight="700" fill="url(#hgold)" letterSpacing="2" filter="url(#hg1)">GLITZ &amp; GLAM</text>
    <line x1="80" y1="136" x2="600" y2="136" stroke="url(#hhline)" strokeWidth="0.5"/>
    <g transform="translate(340,136)">
      <path d="M0,-5 L3.5,0 L0,5 L-3.5,0 Z" fill="url(#hgold)" filter="url(#hg1)" opacity="0.85"/>
      <path d="M-10,-3 L-7,0 L-10,3 L-13,0 Z" fill="url(#hgold)" opacity="0.35"/>
      <path d="M10,-3 L13,0 L10,3 L7,0 Z" fill="url(#hgold)" opacity="0.35"/>
    </g>
    <text x="272" y="173" textAnchor="middle" fontFamily="Georgia,serif" fontSize="15" fontWeight="400" fontStyle="italic" fill="url(#hgold)" letterSpacing="4" opacity="0.75">by</text>
    <text x="374" y="176" textAnchor="middle" fontFamily="'Palatino Linotype','Palatino','Book Antiqua',Georgia,serif" fontSize="32" fontWeight="700" fontStyle="italic" fill="url(#hjyoti)" letterSpacing="3" filter="url(#hg2)">Jyoti</text>
    <g transform="translate(428,163)" filter="url(#hhg)">
      <path d="M0,10 C-2,7 -9,3 -9,-2 C-9,-7 -5,-10 0,-7 C5,-10 9,-7 9,-2 C9,3 2,7 0,10 Z" fill="url(#hheart)" opacity="0.92"/>
    </g>
    <line x1="318" y1="183" x2="434" y2="183" stroke="rgba(160,50,80,0.4)" strokeWidth="0.6"/>
  </svg>
);

const Header = () => {
  const { i18n } = useTranslation();
  const isMobile = useIsMobile();
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const cartItems = useCartStore((state) => state.items);
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const toggleLang = () => {
    const l = i18n.language === 'en' ? 'hi' : 'en';
    i18n.changeLanguage(l);
    localStorage.setItem('language', l);
  };

  // Feature #7 — Pink theme toggle
  const [isPink, setIsPink] = React.useState(() => {
    const saved = localStorage.getItem('site_theme');
    // Only pink if EXPLICITLY saved as pink
    return saved === 'pink';
  });

  // On mount — ensure dark is default if nothing saved
  React.useEffect(() => {
    const saved = localStorage.getItem('site_theme');
    if (!saved || saved === 'dark') {
      localStorage.setItem('site_theme', 'dark');
      document.documentElement.classList.remove('theme-pink');
    }
  }, []);
  const toggleTheme = () => {
    const next = !isPink;
    setIsPink(next);
    localStorage.setItem('site_theme', next ? 'pink' : 'dark');
    document.documentElement.classList.toggle('theme-pink', next);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  // Fetch user's points balance for the header pill (logged-in users only).
  // Fails silently — if rewards API isn't deployed yet, pill just won't show.
  // MUST be declared before any early return (Rules of Hooks).
  const [pointsBalance, setPointsBalance] = useState(0);
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { setPointsBalance(0); return; }
    const API = process.env.REACT_APP_API_URL || 'https://api.glitzandglambyjyoti.com';
    fetch(`${API}/api/rewards/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d && typeof d.balance === 'number') setPointsBalance(d.balance); })
      .catch(() => {});
  }, [location.pathname]);

  if (isAdmin) return null;

  const NAV = [
    { to: '/', label: i18n.language === 'hi' ? 'होम' : 'Home' },
    { to: '/shop', label: i18n.language === 'hi' ? 'शॉप' : 'Shop' },
    { to: '/blogs', label: i18n.language === 'hi' ? 'ब्लॉग' : 'Blogs' },
    { to: '/shop-by-video', label: i18n.language === 'hi' ? 'वीडियो शॉप' : 'Shop by Video' },
    { to: '/about-jyoti', label: i18n.language === 'hi' ? 'ज्योति के बारे में' : 'About Jyoti' },
  ];

  return (
    <>
      {/* Announcement bar — scrolling marquee */}
      <div style={S.announce}>
        <div style={S.marqueeTrack}>
          {[1,2].map(i => (
            <span key={i} style={S.announceText}>
              ✦ &nbsp;{i18n.language === 'hi' ? '₹999 से ऊपर मुफ्त शिपिंग' : 'Free Shipping on orders above ₹999'}&nbsp; ✦ &nbsp;{i18n.language === 'hi' ? 'नया कलेक्शन आया!' : 'New Collection Arrived!'}&nbsp; ✦ &nbsp;{i18n.language === 'hi' ? '20% छूट के लिए GLAM20 उपयोग करें' : 'Use GLAM20 for 20% OFF'}&nbsp; ✦ &nbsp;{i18n.language === 'hi' ? 'प्रीमियम ज्वेलरी' : 'Premium Artificial Jewelry'}&nbsp; ✦ &nbsp;{i18n.language === 'hi' ? '7 दिन वापसी नीति' : '7-Day Easy Returns'}&nbsp; &nbsp;&nbsp;&nbsp;
            </span>
          ))}
        </div>
      </div>

      <header style={{ ...S.header, ...(scrolled ? S.headerScrolled : {}) }}>
        <div style={S.container}>
          {/* Logo */}
          <Link to="/" style={{ flexShrink: 0 }}>
            <Logo width={isMobile ? 160 : 220} />
          </Link>

          {/* Desktop Nav */}
          <nav style={S.nav}>
            {NAV.map(n => (
              <Link key={n.to} to={n.to} style={{ ...S.navLink, ...(location.pathname === n.to ? S.navLinkActive : {}) }}>
                {n.label}
                {location.pathname === n.to && <span style={S.navDot}/>}
              </Link>
            ))}
          </nav>

          {/* Actions */}
          <div style={S.actions}>
            {/* Search */}
            <button style={S.iconBtn} onClick={() => setSearchOpen(!searchOpen)} title="Search">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
            </button>

            {/* Language */}
            <button style={S.langBtn} onClick={toggleLang}>
              {i18n.language === 'en' ? 'हिंदी' : 'EN'}
            </button>

            {/* Theme toggle removed — Dark theme only */}

            {/* Wishlist */}
            <Link to="/wishlist" style={S.iconBtn} title="Wishlist">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
              </svg>
            </Link>

            {/* Points pill — only renders for logged-in users with >0 points */}
            {pointsBalance > 0 && (
              <Link
                to="/account/rewards"
                title={`${pointsBalance} points — view rewards`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '5px 11px',
                  background: 'linear-gradient(135deg, #f5d782, #d4af37)',
                  color: '#0a0a1a',
                  borderRadius: 14,
                  fontWeight: 700,
                  fontSize: 12,
                  textDecoration: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                ✨ {pointsBalance.toLocaleString('en-IN')}
              </Link>
            )}

            {/* Account */}
            <Link to="/account" style={S.iconBtn} title="Account">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
              </svg>
            </Link>

            {/* Cart */}
            <Link to="/cart" style={{ ...S.iconBtn, position: 'relative' }} title="Cart">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/>
              </svg>
              {cartItems.length > 0 && (
                <span style={S.cartBadge}>{cartItems.length}</span>
              )}
            </Link>

            {/* Mobile hamburger */}
            <button style={{ ...S.iconBtn, display: 'none' }} id="hamburger" aria-label={mobileOpen ? 'Close menu' : 'Open menu'} aria-expanded={mobileOpen} onClick={() => setMobileOpen(!mobileOpen)}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                {mobileOpen
                  ? <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>
                  : <><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></>
                }
              </svg>
            </button>
          </div>
        </div>

        {/* Search Bar */}
        {searchOpen && (
          <div style={S.searchBar}>
            <form onSubmit={handleSearch} style={S.searchForm}>
              <input
                autoFocus
                style={S.searchInput}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder={i18n.language === 'hi' ? 'ज्वेलरी, साड़ी, बैग खोजें...' : 'Search jewelry, sarees, bags...'}
              />
              <button type="submit" style={S.searchBtn}>Search</button>
              <button type="button" style={S.searchClose} onClick={() => setSearchOpen(false)}>✕</button>
            </form>
          </div>
        )}

        {/* Mobile Menu */}
        {mobileOpen && (
          <div style={S.mobileMenu}>
            {NAV.map(n => (
              <Link key={n.to} to={n.to} style={S.mobileLink} onClick={() => setMobileOpen(false)}>{n.label}</Link>
            ))}
            <button style={S.mobileLang} onClick={toggleLang}>{i18n.language === 'en' ? 'हिंदी में देखें' : 'View in English'}</button>
          </div>
        )}
      </header>

      <style>{`
        @media (max-width: 768px) {
          #hamburger { display: flex !important; }
          nav { display: none !important; }
        }
        .main-content { padding-top: 120px; }
      `}</style>
    </>
  );
};

const S = {
  announce: {
    // Slightly more transparent to blend with hero
    background: 'linear-gradient(90deg, rgba(20,14,36,0.78), rgba(30,20,50,0.78), rgba(20,14,36,0.78))',
    borderBottom: 'none',
    padding: '9px 0',
    overflow: 'hidden',
    position: 'relative',
    zIndex: 101,
    // Fade edges so marquee text doesn't hard-cut at viewport boundaries —
    // creates a smooth visual loop as text scrolls past the right edge
    maskImage:        'linear-gradient(90deg, transparent 0%, black 8%, black 92%, transparent 100%)',
    WebkitMaskImage:  'linear-gradient(90deg, transparent 0%, black 8%, black 92%, transparent 100%)',
  },
  marqueeTrack: {
    display: 'flex',
    width: 'max-content',
    animation: 'marqueeScroll 30s linear infinite',
  },
  announceText: {
    color: 'rgba(240,208,128,0.85)',
    fontSize: '11.5px',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    fontFamily: 'system-ui',
    whiteSpace: 'nowrap',
    paddingRight: '2rem',
  },
  header: {
    position: 'fixed',
    top: 36,
    left: 0,
    right: 0,
    zIndex: 100,
    // Much more transparent at the top — hero image shows through even
    // when hero top edge is dark (model's hair, etc.). Top 15% opacity
    // fades to fully transparent by ~40% down.
    background: 'linear-gradient(180deg, rgba(4,2,10,0.18) 0%, rgba(4,2,10,0.08) 40%, rgba(4,2,10,0) 100%)',
    backdropFilter: 'none',
    WebkitBackdropFilter: 'none',
    borderBottom: 'none',
    transition: 'all 0.3s ease',
  },
  headerScrolled: {
    top: 0,
    // When user scrolls past the hero, we need solid bg for readability
    background: 'rgba(4,2,10,0.92)',
    backdropFilter: 'blur(24px)',
    WebkitBackdropFilter: 'blur(24px)',
    borderBottomColor: 'rgba(201,168,76,0.18)',
    boxShadow: '0 4px 40px rgba(0,0,0,0.4)',
  },
  container: {
    maxWidth: 1280,
    margin: '0 auto',
    padding: '8px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    overflow: 'hidden',
  },
  nav: {
    display: 'flex',
    alignItems: 'center',
    gap: 0,
    flex: 1,
    justifyContent: 'center',
    overflow: 'visible',
  },
  navLink: {
    color: 'rgba(232,232,245,0.7)',
    fontSize: '11px',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    padding: '8px 10px',
    borderRadius: 8,
    transition: 'all 0.2s',
    position: 'relative',
    fontFamily: 'system-ui',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    textDecoration: 'none',
  },
  navLinkActive: {
    color: '#c9a84c',
  },
  navDot: {
    position: 'absolute',
    bottom: 2,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 4,
    height: 4,
    borderRadius: '50%',
    background: '#c9a84c',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    flexShrink: 0,
  },
  iconBtn: {
    background: 'transparent',
    border: 'none',
    color: 'rgba(232,232,245,0.65)',
    padding: '7px',
    borderRadius: 9,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
    cursor: 'pointer',
    textDecoration: 'none',
    flexShrink: 0,
  },
  langBtn: {
    background: 'rgba(201,168,76,0.1)',
    border: '1px solid rgba(201,168,76,0.25)',
    color: '#c9a84c',
    padding: '5px 8px',
    borderRadius: 20,
    fontSize: '10px',
    letterSpacing: '0.5px',
    fontFamily: 'system-ui',
    fontWeight: 600,
    cursor: 'pointer',
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  cartBadge: {
    position: 'absolute',
    top: 4,
    right: 4,
    background: 'linear-gradient(135deg, #c9a84c, #f0d080)',
    color: '#04020a',
    fontSize: '9px',
    fontWeight: 800,
    width: 16,
    height: 16,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'system-ui',
  },
  searchBar: {
    borderTop: '1px solid rgba(201,168,76,0.1)',
    padding: '12px 32px',
    background: 'rgba(4,2,10,0.95)',
  },
  searchForm: {
    maxWidth: 600,
    margin: '0 auto',
    display: 'flex',
    gap: 8,
  },
  searchInput: {
    flex: 1,
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(201,168,76,0.2)',
    borderRadius: 10,
    padding: '10px 16px',
    color: '#e8e8f5',
    fontSize: 14,
    outline: 'none',
    fontFamily: 'system-ui',
  },
  searchBtn: {
    background: 'linear-gradient(135deg, #c9a84c, #f0d080)',
    border: 'none',
    borderRadius: 10,
    padding: '10px 20px',
    color: '#04020a',
    fontWeight: 700,
    fontSize: 13,
    letterSpacing: '0.5px',
    fontFamily: 'system-ui',
  },
  searchClose: {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 10,
    padding: '10px 14px',
    color: '#888',
    fontSize: 14,
  },
  mobileMenu: {
    borderTop: '1px solid rgba(201,168,76,0.1)',
    padding: '16px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  mobileLink: {
    color: 'rgba(232,232,245,0.7)',
    fontSize: 14,
    padding: '12px 16px',
    borderRadius: 8,
    letterSpacing: '1px',
    textTransform: 'uppercase',
    fontFamily: 'system-ui',
  },
  mobileLang: {
    background: 'rgba(201,168,76,0.1)',
    border: '1px solid rgba(201,168,76,0.2)',
    color: '#c9a84c',
    padding: '10px 16px',
    borderRadius: 8,
    fontSize: 13,
    marginTop: 8,
    textAlign: 'center',
    fontFamily: 'system-ui',
  },
};

// Inject marquee keyframe directly — guarantees it works
const _s = document.createElement('style');
_s.textContent = `@keyframes marqueeScroll { 0%{transform:translateX(0)} 100%{transform:translateX(-50%)} }`;
document.head.appendChild(_s);

export default Header;
